import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Activity, Globe, Gauge } from "lucide-react";

const CLIENTS = {
  bellavista: { name: "Ristorante Bellavista", uptime: "99.98%", speed: 98, keywords: 128, growth: "+34.2%" },
  rossi: { name: "Studio Tecnico Rossi", uptime: "99.95%", speed: 94, keywords: 86, growth: "+21.7%" },
  fitlife: { name: "FitLife Gym", uptime: "99.99%", speed: 96, keywords: 152, growth: "+48.9%" },
};

export default function ClientAreaPreview() {
  const [active, setActive] = useState("bellavista");
  const navigate = useNavigate();
  const c = CLIENTS[active];

  return (
    <section id="area-cliente" data-testid="client-area-preview" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5">
          <p className="mono-label text-[#E05638] mb-6">04 // Area Cliente</p>
          <h2 className="font-display font-bold tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl">
            La tua dashboard, in tempo reale
          </h2>
          <p className="mt-6 text-neutral-600 leading-relaxed text-base max-w-md">
            Stato del sito, report SEO e abbonamenti: tutto sotto controllo da un'unica area
            riservata. Prova la demo con i nostri clienti tipo.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {Object.entries(CLIENTS).map(([id, cl]) => (
              <button
                key={id}
                data-testid={`preview-client-${id}`}
                onClick={() => setActive(id)}
                className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-all duration-300 ${
                  active === id
                    ? "bg-[#111111] border-[#111111] text-white"
                    : "bg-white border-black/15 text-neutral-700 hover:border-[#111111]"
                }`}
              >
                {cl.name}
              </button>
            ))}
          </div>
          <button
            data-testid="preview-cta-client-area"
            onClick={() => navigate("/accedi")}
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#E05638] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#C8452A] transition-colors duration-300"
          >
            Accedi all'area clienti
            <ArrowRight size={16} />
          </button>
        </div>

        <motion.div
          key={active}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 rounded-2xl bg-[#1E293B] text-white p-8 sm:p-10 shadow-[0_40px_80px_-20px_rgba(30,41,59,0.45)]"
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="mono-label text-neutral-400">Dashboard live</p>
              <p className="font-display font-bold text-xl mt-2">{c.name}</p>
            </div>
            <span className="inline-flex items-center gap-2 text-xs font-medium bg-emerald-400/15 text-emerald-300 rounded-full px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Online
            </span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              { icon: Globe, label: "Uptime", value: c.uptime },
              { icon: Gauge, label: "Speed score", value: `${c.speed}/100` },
              { icon: Activity, label: "Keyword posizionate", value: c.keywords },
            ].map((m) => (
              <div key={m.label} className="rounded-xl bg-white/5 border border-white/10 p-5">
                <m.icon size={18} className="text-[#E05638]" />
                <p className="font-display font-extrabold text-xl sm:text-2xl mt-4">{m.value}</p>
                <p className="text-xs text-neutral-400 mt-1.5">{m.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-5 flex items-center justify-between">
            <div>
              <p className="text-xs text-neutral-400">Crescita traffico organico (6 mesi)</p>
              <p className="font-display font-extrabold text-2xl text-[#E05638] mt-1">{c.growth}</p>
            </div>
            <div className="flex items-end gap-1.5 h-16">
              {[35, 45, 42, 58, 66, 82].map((h, i) => (
                <motion.span
                  key={i}
                  initial={{ height: 0 }}
                  animate={{ height: `${h}%` }}
                  transition={{ delay: 0.2 + i * 0.08, duration: 0.5 }}
                  className="w-3 rounded-sm bg-gradient-to-t from-[#E05638]/40 to-[#E05638]"
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
